/**
 * @fileoverview The single URL segment, and what it turns out to be.
 *
 * `pages.cloistr.xyz/<one segment>` is both a page address and an author
 * address, because both are NIP-19 identifiers and the identifier says which it
 * is. There is no `/p/` prefix and there should not be one: a prefix would make
 * our URL shape carry information that the identifier already carries, and the
 * reader would have to know which prefix to use for something they were handed.
 *
 * TWO THINGS ARE DIVERTED. An `npub` or `nprofile` is an author index, and an
 * address for the collection kind is a site. EVERYTHING ELSE goes to the page
 * route unchanged -- a page address, an address of a kind we cannot render, a
 * malformed string, and a private key somebody pasted into a URL. That is
 * deliberate: the page route collapses all of those into one identical "not
 * available", with a timing floor, so that a private page cannot be
 * distinguished from an address that names nothing.
 *
 * THE RULE THAT GOVERNS ADDING A DIVERSION, because this comment previously said
 * only a profile was diverted and a later reader will want to add a third. The
 * danger is not a second path, it is a FASTER one: if an address of a divertable
 * kind resolved to "not available" quicker than a page address did, the
 * difference in timing would say which kind the address named, and for a private
 * page that is the leak this whole design refuses.
 *
 * A collection is safe to divert because `loadCollection` holds every failure to
 * the same floor `loadPage` does, and because the kind is already in the
 * reader's hand: an address names its own kind, so anyone holding it decodes
 * that locally without asking us. Dispatching on it tells them nothing they did
 * not arrive with. Any third diversion has to clear both of those.
 *
 * A BARE NAME IS THE THIRD, and it clears both. `/<name>` is an author index
 * for a name our own NIP-05 domain serves. It is decided by the string's shape,
 * which the reader already holds, and a name nobody holds falls through to the
 * page route and its floor, so it is never faster than any other address. What
 * looks like a key or an address is never a name: a pasted private key (bech32
 * or 64 hex) must not be sent to a name service, even ours.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AuthorRoute } from './AuthorRoute';
import { CollectionRoute } from './CollectionRoute';
import { PageRoute } from './PageRoute';

import { decodeIdentifier, type Identifier } from '@/services/nostr/identifiers';
import { resolveName, type ResolvedName } from '@/services/nostr/nip05';
import { PAGE_COLLECTION_KIND } from '@/services/pages/kinds';

/** The NIP-05 local-part grammar, as `resolveName` applies it. */
const NAME = /^[a-z0-9._-]{1,64}$/;
/** Shapes that are keys or addresses, never names, whatever else they match. */
const NOT_A_NAME = /^(?:n(?:pub|sec|ote|profile|event|addr|relay|cryptsec)1|[0-9a-f]{64}$)/;

export function isBareName(segment: string): boolean {
  const s = segment.toLowerCase();
  return NAME.test(s) && !NOT_A_NAME.test(s);
}

/** A name, resolved: their index if someone holds it, the page route if not. */
function NameRoute({ name }: { name: string }) {
  const [resolved, setResolved] = useState<ResolvedName | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    setResolved(undefined);
    void resolveName(name).then((r) => {
      if (live) setResolved(r);
    });
    return () => {
      live = false;
    };
  }, [name]);

  if (resolved === undefined) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }
  if (resolved) return <AuthorRoute pubkey={resolved.pubkey} hints={resolved.relays} />;
  return <PageRoute naddr={name} />;
}

export function SegmentRoute() {
  const { id = '' } = useParams();

  let decoded: Identifier | null = null;
  try {
    decoded = decodeIdentifier(id);
  } catch {
    // An nsec. Not diverted, not named, not echoed -- it falls through to the
    // page route, which refuses it the same way it refuses everything else.
    decoded = null;
  }

  if (decoded?.type === 'profile') {
    return <AuthorRoute pubkey={decoded.pubkey} hints={decoded.relays} />;
  }

  if (decoded?.type === 'address' && decoded.kind === PAGE_COLLECTION_KIND) {
    return <CollectionRoute naddr={id} />;
  }

  if (!decoded && isBareName(id)) return <NameRoute name={id} />;

  return <PageRoute naddr={id} />;
}
